/**
 * Sprint 6 C4 (EFISSUE-817 Phase 2) — charter-first config.
 *
 * Five values that previously lived as hardcoded constants in
 * `relayActionItems.ts` now flow from `relay_workspaces/default
 * .charter_rules`. The Charter (TEAM_CHARTER.md) is the human-readable
 * source; this object is the runtime config the UI reads.
 *
 * Defaults baked in code so a fresh workspace, a Firestore read failure,
 * or a partially-seeded doc all yield the current behavior — zero
 * regression. App.tsx seeds the doc on first load via the same migration
 * pattern C3 uses.
 */
import {
  AWAITING_WINDOW_MS,
  CRITICAL_TAGS,
  URGENT_THRESHOLD_MS,
  type ActionItemRules,
} from "./relayActionItems";

export type CharterRules = ActionItemRules;

export const DEFAULT_CHARTER_RULES: CharterRules = {
  action_required_tags: [...CRITICAL_TAGS],
  parked_tags: ["parked"],
  awaiting_window_ms: AWAITING_WINDOW_MS,
  urgent_threshold_ms: URGENT_THRESHOLD_MS,
  agent_authors: ["claude-code", "advisor"],
};

/**
 * Merge a partial Firestore doc against the defaults. Anything missing
 * or non-array-shaped falls back to default. Unknown keys on `partial`
 * are ignored (forward-compat: future schema additions don't crash
 * older clients).
 */
export function mergeCharterRules(partial: unknown): CharterRules {
  if (!partial || typeof partial !== "object") return { ...DEFAULT_CHARTER_RULES };
  const p = partial as Record<string, unknown>;
  return {
    action_required_tags: arrayOrDefault(p.action_required_tags, DEFAULT_CHARTER_RULES.action_required_tags),
    parked_tags: arrayOrDefault(p.parked_tags, DEFAULT_CHARTER_RULES.parked_tags),
    awaiting_window_ms: numberOrDefault(p.awaiting_window_ms, DEFAULT_CHARTER_RULES.awaiting_window_ms),
    urgent_threshold_ms: numberOrDefault(p.urgent_threshold_ms, DEFAULT_CHARTER_RULES.urgent_threshold_ms),
    agent_authors: arrayOrDefault(p.agent_authors, DEFAULT_CHARTER_RULES.agent_authors),
  };
}

/**
 * True when the workspace doc is missing any of the five expected keys
 * (or the whole `charter_rules` field is absent). False once all five
 * are present — idempotent on repeat snapshot fires.
 */
export function needsSeed(partial: unknown): boolean {
  if (!partial || typeof partial !== "object") return true;
  const p = partial as Record<string, unknown>;
  return (
    !Array.isArray(p.action_required_tags)
    || !Array.isArray(p.parked_tags)
    || typeof p.awaiting_window_ms !== "number"
    || typeof p.urgent_threshold_ms !== "number"
    || !Array.isArray(p.agent_authors)
  );
}

function arrayOrDefault(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return [...fallback];
  const filtered = value.filter((v): v is string => typeof v === "string");
  return filtered.length > 0 ? filtered : [...fallback];
}

function numberOrDefault(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
